import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	AppKeyService,
	IRequiredAppVersions,
	IRequiredAppVersion,
	RequiredAppVersionAction
} from '@app/services/appkey';
import { FeedbackData, FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';

import { AppKeyRequiredVersionsDialogData } from './appkey-required-versions.dialog.models';
import { AppKeyRequiredVersionEditDialogComponent } from './appkey-required-version-edit.dialog';
import { AppKeyRequiredVersionEditDialogData } from './appkey-required-version-edit-dialog.models';
import { AppKeyRequiredVersionPlatformCreateDialogComponent } from './appkey-required-version-platform-create.dialog';

@Component({
	selector: 'app-appkey-required-versions-dialog',
	templateUrl: './appkey-required-versions.dialog.html',
	styleUrls: ['./appkey-required-versions.dialog.scss']
})
export class AppKeyRequiredVersionsDialogComponent implements OnInit {
	public loading = true;
	public versions: Record<string, IRequiredAppVersion[]> = {};
	public platforms: string[] = [];
	public openedMenu: number | null = null;

	public readonly actionOrder: RequiredAppVersionAction[] = [
		RequiredAppVersionAction.forcedImmediately,
		RequiredAppVersionAction.forcedNextConvenient,
		RequiredAppVersionAction.recommended
	];

	constructor(
		@Inject(MAT_DIALOG_DATA) public dialogData: AppKeyRequiredVersionsDialogData,
		private appKeyService: AppKeyService,
		private feedbackService: FeedbackService,
		private dialogRef: MatDialogRef<AppKeyRequiredVersionsDialogComponent>,
		private dialog: MatDialog
	) {}

	public ngOnInit(): void {
		this.appKeyService.getRequiredVersions(this.dialogData.appKey.clientId).subscribe(
			(versions: IRequiredAppVersions) => {

				// flatten the dictionary of string -> dictionary into a dictionary of string -> array so that we can use the values as a DataSource
				this.versions = Object.entries(versions || {}).reduce(
					(acc, [platform, versionMap]) => {
						acc[platform] = Object.values(versionMap)
							.sort((a, b) => this.actionOrder.indexOf(a.action) - this.actionOrder.indexOf(b.action));
						return acc;
					},
					{} as Record<string, IRequiredAppVersion[]>
				);

				this.platforms = Object.keys(this.versions || {});

				this.loading = false;
			},
			(error: any) => {
				this.loading = false;
				this.feedbackService.apiError(error);
			}
		);
	}

	public onDeleteVersion(platform: string, action: RequiredAppVersionAction): void {

		const data: FeedbackData = {
			title: FeedbackTitle.warning,
			message: `Are you sure you wish to delete the Required Version? This cannot be undone.`,
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.deleteVersion(platform, action);
				}
			}
		};

		this.feedbackService.confirm(data);
	}

	private deleteVersion(platform: string, action: RequiredAppVersionAction): void {
		this.appKeyService.deleteRequiredVersion(this.dialogData.appKey.clientId, platform, action).subscribe(
			() => {
				this.versions[platform] = this.versions[platform].filter(v => v.action !== action);

				if (this.versions[platform].length === 0) {
					delete this.versions[platform];
					this.platforms = Object.keys(this.versions || {});
				}

				this.versions = { ...this.versions };

				this.feedbackService.notification({
					msg: 'Required Version deleted successfully'
				});
			},
			(error: any) => {
				this.feedbackService.apiError(error);
			}
		);
	}

	public onEditVersion(platform: string, version: IRequiredAppVersion): void {
		const dialogData: AppKeyRequiredVersionEditDialogData = {
			appKey: this.dialogData.appKey,
			platform: platform,
			version: version,
			mode: 'edit'
		};

		this.dialog.open(AppKeyRequiredVersionEditDialogComponent, {
			width: '600px',
			data: dialogData
		}).afterClosed().subscribe((updatedVersion: IRequiredAppVersion | null) => {
			if (updatedVersion) {
				// Update the version in the versions object
				this.versions[platform] = this.versions[platform].map(v =>
					v.action === version.action ? updatedVersion : v
				);
			}
		});
	}

	public onCreateNewVersion(platform: string): void {
		const dialogData: AppKeyRequiredVersionEditDialogData = {
			appKey: this.dialogData.appKey,
			platform: platform,
			mode: 'create'
		};

		this.dialog.open(AppKeyRequiredVersionEditDialogComponent, {
			width: '600px',
			data: dialogData
		}).afterClosed().subscribe((newVersion: IRequiredAppVersion | null) => {
			if (newVersion) {
				// Add the new version to the versions array
				this.versions[platform].push(newVersion);

				// Re-sort to maintain order
				this.versions[platform].sort((a, b) =>
					this.actionOrder.indexOf(a.action) - this.actionOrder.indexOf(b.action)
				);

				// Make sure there are no duplicate actions
				this.versions[platform] = this.versions[platform].filter(v => v.action !== newVersion.action || v === newVersion);
				this.versions = { ...this.versions };
			}
		});
	}

	public openCreatePlatformDialog(): void {
		this.dialog.open(AppKeyRequiredVersionPlatformCreateDialogComponent, {
			width: '420px'
		}).afterClosed().subscribe((platform: string | null) => {
			if (platform) {
				this.addPlatform(platform);
			}
		});
	}

	private addPlatform(platform: string): void {
		const normalizedPlatform = (platform || '').trim();

		if (!normalizedPlatform) return;

		if (this.versions[normalizedPlatform]) {
			this.feedbackService.notification({
				msg: 'Platform already exists'
			});
			return;
		}

		this.versions = {
			...this.versions,
			[normalizedPlatform]: []
		};
		this.platforms = [...this.platforms, normalizedPlatform];
	}

	public close(): void {
		this.dialogRef.close();
	}
}
